import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ChevronLeft, ChevronRight, Clock3, LayoutGrid, Rows, Search, X } from 'lucide-react';
import { useRestaurant } from '../../context/RestaurantContext';
import { useSiteWorkspace } from '../../context/SiteWorkspaceContext';
import SmartImagePreview from '../../components/shared/SmartImagePreview';
import { buildPublicMenuSections, flattenPublicItemsInSectionOrder } from '../../utils/publicMenu';
import { getItemSearchScore, getSmartSuggestions } from '../../utils/menuSearch';

type ViewMode = 'list' | 'grid';

const MenuPage: React.FC = () => {
  const { restaurant, formatMoney } = useRestaurant();
  const { categories, items } = useSiteWorkspace();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSection, setSelectedSection] = useState<string | null>(searchParams.get('section'));
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [canScrollRailLeft, setCanScrollRailLeft] = useState(false);
  const [canScrollRailRight, setCanScrollRailRight] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') {
      return 'list';
    }

    return window.localStorage.getItem('arbali-menu-view') === 'grid' ? 'grid' : 'list';
  });
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const sectionRailRef = useRef<HTMLDivElement>(null);
  const selectedSectionButtonRef = useRef<HTMLButtonElement | null>(null);
  const searchPanelRef = useRef<HTMLDivElement>(null);

  const menuSections = useMemo(() => buildPublicMenuSections(categories, items), [categories, items]);
  const smartSuggestions = useMemo(
    () => getSmartSuggestions(menuSections, deferredSearchTerm),
    [deferredSearchTerm, menuSections],
  );

  const updateSectionRailState = () => {
    const rail = sectionRailRef.current;
    if (!rail) {
      setCanScrollRailLeft(false);
      setCanScrollRailRight(false);
      return;
    }

    const maxScrollLeft = rail.scrollWidth - rail.clientWidth;
    setCanScrollRailLeft(rail.scrollLeft > 8);
    setCanScrollRailRight(maxScrollLeft > 8 && rail.scrollLeft < maxScrollLeft - 8);
  };

  useEffect(() => {
    if (selectedSectionButtonRef.current) {
      selectedSectionButtonRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    } else if (!selectedSection && sectionRailRef.current) {
      sectionRailRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
    window.setTimeout(updateSectionRailState, 160);
  }, [selectedSection]);

  useEffect(() => {
    updateSectionRailState();

    const rail = sectionRailRef.current;
    if (!rail) {
      return undefined;
    }

    const handleResize = () => updateSectionRailState();
    rail.addEventListener('scroll', handleResize, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      rail.removeEventListener('scroll', handleResize);
      window.removeEventListener('resize', handleResize);
    };
  }, [menuSections.length]);

  useEffect(() => {
    setActiveSuggestionIndex(0);
  }, [deferredSearchTerm, selectedSection, smartSuggestions.length]);

  useEffect(() => {
    if (!showSuggestions) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!searchPanelRef.current?.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, [showSuggestions]);

  useEffect(() => {
    window.localStorage.setItem('arbali-menu-view', viewMode);
  }, [viewMode]);

  const daypartLabel = useMemo(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 11) return 'Breakfast time';
    if (hour >= 11 && hour < 15) return 'Lunch time';
    if (hour >= 15 && hour < 18) return 'Afternoon pick';
    return 'Dinner time';
  }, []);

  const filteredSections = useMemo(() => {
    const normalizedQuery = deferredSearchTerm.trim().toLowerCase();

    return menuSections
      .filter((section) => !selectedSection || section.id === selectedSection)
      .map((section) => {
        const sectionItems = section.items.filter((item) => {
          if (!normalizedQuery) return true;
          return getItemSearchScore(item, section, normalizedQuery) >= 0;
        });

        return { ...section, items: sectionItems };
      })
      .filter((section) => section.items.length > 0);
  }, [deferredSearchTerm, selectedSection, menuSections]);

  const totalMatches = useMemo(
    () => filteredSections.reduce((sum, section) => sum + section.items.length, 0),
    [filteredSections],
  );

  const featuredResults = useMemo(() => flattenPublicItemsInSectionOrder(menuSections).slice(0, 3), [menuSections]);
  const filteredItems = useMemo(
    () =>
      filteredSections.flatMap((section) =>
        section.items.map((item) => ({
          item,
          section,
        })),
      ),
    [filteredSections],
  );

  const handleSectionSelect = (sectionId: string | null) => {
    setSelectedSection(sectionId);
    if (sectionId) {
      setSearchParams({ section: sectionId });
      return;
    }
    setSearchParams({});
  };

  const scrollSectionRail = (direction: 'left' | 'right') => {
    const rail = sectionRailRef.current;
    if (!rail) {
      return;
    }

    rail.scrollBy({
      left: direction === 'left' ? -260 : 260,
      behavior: 'smooth',
    });
  };

  const runSuggestionAction = (suggestion: (typeof smartSuggestions)[number]) => {
    setShowSuggestions(false);

    if (suggestion.itemId) {
      navigate(`/item/${suggestion.itemId}`);
      return;
    }

    if (suggestion.sectionId) {
      handleSectionSelect(suggestion.sectionId);
    }

    if (suggestion.query) {
      setSearchTerm(suggestion.query);
      return;
    }

    if (!suggestion.sectionId) {
      setSearchTerm('');
    }
  };

  const handleSuggestionSelect = (suggestion: (typeof smartSuggestions)[number]) => {
    runSuggestionAction(suggestion);
  };

  return (
    <div className="overflow-x-hidden px-4 pb-24">
      <div className="mx-auto max-w-6xl">
        <div className="mb-5 pt-4">
          <div className="arab-panel min-w-0 p-4 sm:p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.35em] text-amber-100/45">Menu</div>
                <h1 className="mt-1.5 font-heading text-[2rem] font-semibold leading-none text-white sm:text-4xl">
                  {restaurant.name}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <div className="text-sm text-white/52">{totalMatches} items</div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/16 bg-amber-400/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-amber-100/85">
                    <Clock3 className="h-3.5 w-3.5" />
                    {daypartLabel}
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1">
                  <button
                    type="button"
                    onClick={() => setViewMode('list')}
                    className={`flex h-9 w-9 items-center justify-center rounded-full transition ${
                      viewMode === 'list' ? 'bg-amber-300 text-[#2b1209]' : 'text-white/55 hover:text-white'
                    }`}
                    aria-label="List view"
                  >
                    <Rows className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('grid')}
                    className={`flex h-9 w-9 items-center justify-center rounded-full transition ${
                      viewMode === 'grid' ? 'bg-amber-300 text-[#2b1209]' : 'text-white/55 hover:text-white'
                    }`}
                    aria-label="Grid view"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </button>
                </div>
                <Link
                  to="/qr-code"
                  className="shrink-0 rounded-full border border-amber-300/14 bg-amber-300/8 px-3 py-2 text-[10px] uppercase tracking-[0.24em] text-amber-200 transition hover:border-amber-300/24 hover:bg-amber-300/12"
                >
                  QR
                </Link>
              </div>
            </div>

            <div ref={searchPanelRef} className="relative mt-3">
              <input
                type="text"
                placeholder="Search breakfast, pizza, machiato, shawrma..."
                value={searchTerm}
                onFocus={() => setShowSuggestions(true)}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSuggestions(true);
                  setActiveSuggestionIndex(0);
                }}
                onKeyDown={(event) => {
                  if (!smartSuggestions.length) {
                    if (event.key === 'Escape') {
                      setShowSuggestions(false);
                    }
                    return;
                  }

                  if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    setShowSuggestions(true);
                    setActiveSuggestionIndex((currentIndex) => (currentIndex + 1) % smartSuggestions.length);
                    return;
                  }

                  if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    setShowSuggestions(true);
                    setActiveSuggestionIndex(
                      (currentIndex) => (currentIndex - 1 + smartSuggestions.length) % smartSuggestions.length,
                    );
                    return;
                  }

                  if (event.key === 'Enter' && showSuggestions) {
                    event.preventDefault();
                    handleSuggestionSelect(smartSuggestions[activeSuggestionIndex] ?? smartSuggestions[0]);
                    return;
                  }

                  if (event.key === 'Escape') {
                    setShowSuggestions(false);
                  }
                }}
                className="w-full rounded-2xl border border-white/10 bg-[#140907] px-4 py-3 pl-11 text-white placeholder:text-white/40 focus:border-amber-400/40 focus:outline-none"
              />
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/40" />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setShowSuggestions(true);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 transition-colors hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
              {showSuggestions && smartSuggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-[calc(100%+0.75rem)] z-20 overflow-hidden rounded-[24px] border border-white/10 bg-[#1a0a08]/98 shadow-[0_18px_50px_rgba(0,0,0,0.35)] backdrop-blur-xl">
                  <div className="border-b border-white/8 px-4 py-3 text-[11px] uppercase tracking-[0.28em] text-amber-100/50">
                    {deferredSearchTerm.trim() ? 'Suggestions' : 'Good picks right now'}
                  </div>
                  <div className="max-h-[360px] overflow-y-auto p-2">
                    {smartSuggestions.map((suggestion, suggestionIndex) => (
                      <button
                        key={suggestion.id}
                        type="button"
                        onClick={() => handleSuggestionSelect(suggestion)}
                        className={`flex w-full items-center justify-between gap-3 rounded-2xl px-3 py-3 text-left transition ${
                          activeSuggestionIndex === suggestionIndex ? 'bg-white/8' : 'hover:bg-white/6'
                        }`}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          {suggestion.thumbnailSrc ? (
                            <SmartImagePreview
                              src={suggestion.thumbnailSrc}
                              alt={suggestion.title}
                              className="h-14 w-14 shrink-0 rounded-2xl"
                              imageClassName="p-2"
                            />
                          ) : (
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/8 bg-white/5 text-amber-200/80">
                              <Search className="h-4 w-4" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="truncate font-semibold text-white">{suggestion.title}</div>
                            <div className="mt-1 truncate text-sm text-white/50">{suggestion.subtitle}</div>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          {suggestion.priceLabel && (
                            <span className="rounded-full border border-amber-300/12 bg-amber-300/8 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-200/85">
                              {suggestion.priceLabel}
                            </span>
                          )}
                          <ArrowUpRight className="h-4 w-4 text-amber-300/70" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative mt-3 overflow-hidden">
              {canScrollRailLeft && (
                <button
                  type="button"
                  onClick={() => scrollSectionRail('left')}
                  className="absolute left-0 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-[#2a120f]/90 text-amber-100 shadow-[0_10px_24px_rgba(0,0,0,0.3)] backdrop-blur md:flex"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )}

              <div
                ref={sectionRailRef}
                onWheel={(event) => {
                  if (!sectionRailRef.current || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
                    return;
                  }

                  event.preventDefault();
                  sectionRailRef.current.scrollBy({
                    left: event.deltaY,
                  });
                }}
                className="flex w-full max-w-full gap-2 overflow-x-auto px-1 pb-1 pt-1 scrollbar-hide [mask-image:linear-gradient(to_right,transparent,black_2%,black_98%,transparent)] scroll-smooth snap-x snap-mandatory [touch-action:pan-x]"
              >
                <button
                  type="button"
                  onClick={() => handleSectionSelect(null)}
                  ref={!selectedSection ? selectedSectionButtonRef : undefined}
                  className={`arab-filter-pill shrink-0 snap-start ${!selectedSection ? 'arab-filter-pill-active' : ''}`}
                >
                  All Sections
                </button>
                {menuSections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => handleSectionSelect(section.id)}
                    ref={selectedSection === section.id ? selectedSectionButtonRef : undefined}
                    className={`arab-filter-pill shrink-0 snap-start ${selectedSection === section.id ? 'arab-filter-pill-active' : ''}`}
                  >
                    {section.title}
                  </button>
                ))}
              </div>

              {canScrollRailRight && (
                <button
                  type="button"
                  onClick={() => scrollSectionRail('right')}
                  className="absolute right-0 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-[#2a120f]/90 text-amber-100 shadow-[0_10px_24px_rgba(0,0,0,0.3)] backdrop-blur md:flex"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {filteredSections.length === 0 ? (
          <div className="arab-panel py-16 text-center">
            <div className="mb-4 text-6xl">🔍</div>
            <div className="text-2xl font-semibold text-white">No menu matches</div>
            <p className="mt-2 text-white/55">Try another search term or clear the section filter.</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
            {filteredItems.map(({ item, section }, itemIndex) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.02 * itemIndex }}
              >
                <Link
                  to={`/item/${item.id}`}
                  className="group block overflow-hidden rounded-[28px] border border-amber-300/12 bg-[#1d0c0a] shadow-[0_20px_50px_rgba(0,0,0,0.28)] transition-transform hover:-translate-y-1"
                >
                  <div className="relative">
                    <SmartImagePreview
                      src={item.imageUrl || section.coverImage}
                      alt={item.imageAlt || item.name}
                      className="aspect-[0.95] w-full rounded-none border-0"
                      fit="cover"
                    />
                    {!item.isAvailable && (
                      <div className="absolute left-3 top-3 rounded-full border border-red-300/20 bg-red-500/85 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
                        Unavailable
                      </div>
                    )}
                  </div>
                  <div className="p-3.5">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-amber-100/45">{section.title}</div>
                    <div className="mt-2 min-h-[2.8rem] text-base font-semibold leading-5 text-white transition-colors group-hover:text-amber-200">
                      {item.name}
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <div className="arab-price-pill px-3 py-2 text-[11px]">{formatMoney(item.price)}</div>
                      <ArrowUpRight className="h-4 w-4 shrink-0 text-amber-300/70" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {filteredSections.map((section, sectionIndex) => (
              <motion.section
                key={section.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.04 * sectionIndex }}
                className="arab-panel overflow-hidden"
              >
                <div className="grid gap-0 lg:grid-cols-[320px_1fr]">
                  <div className="relative min-h-[220px] overflow-hidden border-b border-white/8 lg:border-b-0 lg:border-r">
                    <img src={section.coverImage} alt={section.title} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#110706] via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <div className="arab-gold-text text-3xl font-semibold">{section.title}</div>
                      <div className="mt-2 max-w-xs text-sm text-white/65">{section.shortDescription}</div>
                    </div>
                  </div>

                  <div className="p-4 sm:p-6">
                    <div className="space-y-3">
                      {section.items.map((item) => (
                        <Link key={item.id} to={`/item/${item.id}`} className="arab-list-row group block">
                          <div className="flex items-center gap-4 pr-4">
                            <SmartImagePreview
                              src={item.imageUrl || section.coverImage}
                              alt={item.imageAlt || item.name}
                              className="h-20 w-20 shrink-0"
                            />
                            <div className="min-w-0">
                              <div className="text-lg font-semibold text-white transition-colors group-hover:text-amber-200">
                                {item.name}
                              </div>
                              {item.description && (
                                <div className="mt-1 max-w-3xl text-sm leading-6 text-white/50">{item.description}</div>
                              )}
                              {!item.isAvailable && (
                                <div className="mt-2 inline-flex rounded-full border border-red-300/20 bg-red-500/10 px-2 py-1 text-[10px] uppercase tracking-[0.25em] text-red-200/90">
                                  Currently unavailable
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="arab-price-pill">{formatMoney(item.price)}</div>
                            <ChevronRight className="h-4 w-4 text-amber-300/60 transition-transform group-hover:translate-x-1" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.section>
            ))}
          </div>
        )}

        {!searchTerm && viewMode === 'list' && (
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            {featuredResults.map((entry) => {
              const sectionTitle =
                menuSections.find((section) => section.id === entry.categoryId)?.title ?? 'Menu';
              return (
                <Link
                  key={entry.id}
                  to={`/item/${entry.id}`}
                  className="arab-panel p-4 transition-transform hover:-translate-y-1"
                >
                  <div className="text-[11px] uppercase tracking-[0.3em] text-amber-100/45">{sectionTitle}</div>
                  <div className="mt-3 text-xl font-semibold text-white">{entry.name}</div>
                  <div className="mt-3 inline-flex arab-price-pill">{formatMoney(entry.price)}</div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuPage;
